import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { History, Download, RefreshCw, Filter, X, Calendar, Search } from 'lucide-react';
import { getStockMovements, voidMovement, exportAuditLogs } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const REASON_LABELS = {
  walk_in_sale: 'Walk-in Sale',
  online_sale: 'Online Sale',
  sampling: 'Sampling',
  breakage: 'Breakage',
  other: 'Other',
  restock: 'Restock',
};

const AuditLogsPage = () => {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [voidingId, setVoidingId] = useState(null);
  const [confirmVoid, setConfirmVoid] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('7days'); // New: date range filter
  const [limit, setLimit] = useState(200);

  const fetchData = useCallback(async () => {
    try {
      const res = await getStockMovements(null, null, limit);
      setMovements(res.data);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleVoid = async () => {
    if (!confirmVoid) return;
    
    setVoidingId(confirmVoid.id);
    try {
      await voidMovement(confirmVoid.id);
      toast.success('Movement voided');
      setConfirmVoid(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to void movement');
    } finally {
      setVoidingId(null);
    }
  };

  const handleExport = async () => {
    try {
      await exportAuditLogs();
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return {
      date: date.toLocaleDateString([], { month: 'short', day: 'numeric' }),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
  };

  const filteredMovements = movements.filter((m) => {
    // Filter by type
    if (filterType !== 'all' && m.movement_type !== filterType) {
      return false;
    }
    
    // Filter by date range
    if (dateRange !== 'all') {
      const movementDate = new Date(m.created_at);
      const now = new Date();
      const daysAgo = {
        '7days': 7,
        '30days': 30,
        '90days': 90,
      }[dateRange];
      
      if (daysAgo) {
        const cutoffDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);
        if (movementDate < cutoffDate) {
          return false;
        }
      }
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return (
        m.product_name.toLowerCase().includes(query) ||
        m.product_sku.toLowerCase().includes(query) ||
        m.user_name.toLowerCase().includes(query) ||
        (m.note && m.note.toLowerCase().includes(query))
      );
    }
    
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-enter" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-heading font-bold">Audit Logs</h1>
          <p className="text-sm text-muted-foreground">
            Showing {filteredMovements.length} of {movements.length} records
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExport}
            className="btn-icon"
            aria-label="Export"
            data-testid="export-logs-btn"
          >
            <Download className="w-5 h-5" />
          </button>
          <button
            onClick={fetchData}
            className="btn-icon"
            aria-label="Refresh"
            data-testid="refresh-logs-btn"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40" data-testid="filter-type-select">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="stock_out">Stock Out</SelectItem>
              <SelectItem value="stock_in">Stock In</SelectItem>
            </SelectContent>
          </Select>

          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="flex-1" data-testid="filter-date-select">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="7days">Last 7 Days</SelectItem>
              <SelectItem value="30days">Last 30 Days</SelectItem>
              <SelectItem value="90days">Last 90 Days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-2">
          <Select value={limit.toString()} onValueChange={(val) => setLimit(parseInt(val))}>
            <SelectTrigger className="w-40" data-testid="filter-limit-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="200">Load 200</SelectItem>
              <SelectItem value="500">Load 500</SelectItem>
              <SelectItem value="1000">Load 1000</SelectItem>
              <SelectItem value="2000">Load 2000</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex-1 flex items-center text-xs text-muted-foreground px-2">
            {limit < movements.length ? 
              `Loaded ${movements.length} • Select higher limit to load more` :
              `All ${movements.length} records loaded`
            }
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input
          placeholder="Search products, SKU, user, or notes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-bar"
          data-testid="logs-search-input"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Logs List */}
      {filteredMovements.length === 0 ? (
        <div className="empty-state py-12">
          <History className="empty-state-icon" />
          <h3 className="empty-state-title">No Logs</h3>
          <p className="empty-state-text">
            {searchQuery ? 'No logs match your search' : 'No movement records found'}
          </p>
        </div>
      ) : (
        <div 
          className="space-y-2" 
          data-testid="logs-list"
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          {filteredMovements.map((movement) => {
            const { date, time } = formatDateTime(movement.created_at);
            return (
              <div
                key={movement.id}
                className={`item-card ${movement.voided ? 'opacity-50 line-through' : ''}`}
                data-testid={`log-${movement.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-12 h-12 rounded-lg flex flex-col items-center justify-center ${
                    movement.movement_type === 'stock_out' ? 'bg-destructive/10' : 'bg-success/10'
                  }`}>
                    <span className={`font-data font-bold text-lg ${
                      movement.movement_type === 'stock_out' ? 'text-destructive' : 'text-success'
                    }`}>
                      {movement.movement_type === 'stock_out' ? '-' : '+'}{movement.quantity}
                    </span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{movement.product_name}</p>
                    <p className="text-xs text-muted-foreground font-data">{movement.product_sku}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className={`reason-badge reason-${movement.reason}`}>
                        {REASON_LABELS[movement.reason] || movement.reason}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        by {movement.user_name}
                      </span>
                    </div>
                    {movement.note && (
                      <p className="text-xs text-muted-foreground mt-1 italic">
                        "{movement.note}"
                      </p>
                    )}
                  </div>

                  <div className="text-right">
                    <p className="text-xs text-muted-foreground font-data">{date}</p>
                    <p className="text-xs text-muted-foreground font-data">{time}</p>
                    {!movement.voided && (
                      <button
                        onClick={() => setConfirmVoid(movement)}
                        className="text-xs text-destructive mt-1 hover:underline"
                        data-testid={`void-${movement.id}`}
                      >
                        Void
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Void Confirmation Dialog */}
      <Dialog open={!!confirmVoid} onOpenChange={() => setConfirmVoid(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Void Movement?</DialogTitle>
            <DialogDescription>
              This will void the {confirmVoid?.movement_type === 'stock_out' ? 'stock out' : 'restock'} of{' '}
              <strong>{confirmVoid?.quantity}x {confirmVoid?.product_name}</strong>. 
              This action is logged and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmVoid(null)}
              className="flex-1"
              data-testid="cancel-void"
            >
              Cancel
            </Button>
            <Button
              onClick={handleVoid}
              disabled={voidingId === confirmVoid?.id}
              className="flex-1 bg-destructive hover:bg-destructive/90 text-white"
              data-testid="confirm-void"
            >
              {voidingId === confirmVoid?.id ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                'Void'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditLogsPage;
